import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';

import TopSection from '../../LT/TopSection'
import { newContractReporting } from '../../../redux/actions/ContractMaintenance/ContractReporting';
import { downloadReportsEndpoint, downloadTargetValueReportsEndpoint, getNewYearContractReportsEndpoint } from '../../../common/endpoints';
import { Loader } from '../../../components/common/Loader';
import DataDetails from './DataDetails';
import ButtonComponent from '../../../components/common/button/Button';
import { setSelectedItem } from '../../../redux/actions/SupportTicket/selectionSlice';
import TabSection from '../../../components/common/TabSection';
import { getTargetValueReports } from '../../../redux/actions/ContractMaintenance/TargetValueReports';
import TargetValueDetails from './TargetValueReports/TargetValueDetails';

const NewYearContractReports = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [seqIds, setSeqIds] = useState([]);
  const [rowsData, setRowsData] = useState(0);
  const [targetValueRowsData, setTargetValueRowsData] = useState(0);
  const [btnDisabled, setIsBtnDisabled] = useState(true);
  const getAllReportingData = useSelector((state) => state?.contractReportingSlice?.data);
  const getAllTVReportsData = useSelector((state) => state?.targetValueReportSlice?.data);
  const { instance } = useMsal();
  const [tab, setTab] = useState(0);
  const userRole = localStorage.getItem("CDRole");

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    if (newValue === 0) {
      getCMReports();
      setIsBtnDisabled(true);
    } else if (newValue === 1) {
      getTVReports();
      setIsBtnDisabled(true);
    }
  };

  const onSelectionChanged = (params) => {
    const selectedRowsOnCurrentPage = [];
    params?.api?.forEachNodeAfterFilterAndSort((node) => {
      if (node.isSelected()) {
        selectedRowsOnCurrentPage.push(node.data);
      }
    });
    dispatch(setSelectedItem(selectedRowsOnCurrentPage));
    const ids = selectedRowsOnCurrentPage?.map(item => item?.contractSeqId);
    setSeqIds(ids);
    selectedRowsOnCurrentPage?.length > 0 ? setIsBtnDisabled(false) : setIsBtnDisabled(true);
  };

  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
      isRowSelectable: (rowNode) =>
        rowNode.data && userRole !== "Viewer"
          ? true
          : false,
    };
  }, [userRole]);

  const handleReportDownload = async (event) => {
    setIsLoading(true);
    event.preventDefault();
    const account = instance?.getAllAccounts()[0]; // get the first account if there are multiple
    instance?.setActiveAccount(account);
    const tokenRequest = {
        scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"], // You can specify additional scopes
    };
    const response = await instance?.acquireTokenSilent(tokenRequest);
    const accessToken = response?.accessToken;

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${downloadReportsEndpoint}`;
    const requestBody = {
      seqIds: seqIds,
    };
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          withCredentials: true,
        },
        responseType: "blob",
      });
      
      if(response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `contract-maintenance-report.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Report downloaded successfully', {
          autoClose: 3000,
        });
        getCMReports();
        setIsBtnDisabled(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        toast.error(response?.errorMessage, {
          autoClose: 3000,
        });
      }
    } catch (error) {
      setIsLoading(false);
      setIsBtnDisabled(true);
      toast.error("Something went wrong, Please try again", {
        autoClose: 3000,
      });
    }
  }

  const handleTargetValueReportDownload = async (event) => {
    setIsLoading(true);
    event.preventDefault();
    const account = instance?.getAllAccounts()[0]; // get the first account if there are multiple
    instance?.setActiveAccount(account);
    const tokenRequest = {
        scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"], // You can specify additional scopes
    };
    const response = await instance?.acquireTokenSilent(tokenRequest);
    const accessToken = response?.accessToken;

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${downloadTargetValueReportsEndpoint}`;
    const requestBody = {
      seqIds: seqIds,
    };
    try {
      const response = await axios.post(url, requestBody, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          withCredentials: true,
        },
        responseType: "blob",
      });
      
      if(response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `target-value-report.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Report downloaded successfully', {
          autoClose: 3000,
        });
        getCMReports();
        setIsBtnDisabled(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        toast.error(response?.errorMessage, {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error:', error.response);
      setIsLoading(false);
      setIsBtnDisabled(true);
      toast.error("Something went wrong, Please try again", {
        autoClose: 3000,
      });
    }
  }

  const getCMReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        newContractReporting({
          dynamicEndpoint: `${getNewYearContractReportsEndpoint}`,
        })
      );
      if (response.meta.requestStatus === "fulfilled") {
        // setReportsData(response.payload.cmChangesHisDtoList);
      } else {
        setIsLoading(false)
        toast.error(response.payload, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const getTVReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getTargetValueReports());
      if (response.meta.requestStatus === "fulfilled") {
      } else {
        setIsLoading(false)
        toast.error(response.payload, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    getCMReports();
  }, [getCMReports])

  // const downloadCMTrackingReport = async (event) => {
  //     event.preventDefault();
  //     setIsLoading(true);
  //     const account = instance?.getAllAccounts()[0];
  //     instance?.setActiveAccount(account);
  //     const tokenRequest = {
  //       scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"],
  //     };
  //     try {
  //       const tokenResponse = await instance?.acquireTokenSilent(tokenRequest);
  //       const accessToken = tokenResponse?.accessToken;
  
  //       const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/${downloadCMTrackingReportEndpoint}`;
  //       const response = await axios.get(
  //         url,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${accessToken}`,
  //             withCredentials: true,
  //           },
  //           responseType: "blob", // Important for Excel download
  //         }
  //       );
  
  //       if (response.status === 200) {
  //         const blob = new Blob([response.data], {
  //           type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //         });
  //         const downloadUrl = window.URL.createObjectURL(blob);
  //         const link = document.createElement("a");
  //         link.href = downloadUrl;
  //         link.setAttribute(
  //           "download",
  //           `contract-tracking-report.xlsx`
  //         );
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);
  //         window.URL.revokeObjectURL(downloadUrl);
  //         setIsLoading(false);
  //         // fetchContractsDataFromApi();
  //         // fetchPMDataFromApi();
  //       }
  //     } catch (error) {
  //       console.error("Error downloading contract tracking report:", error);
  //     }
  //   };

  return (
    <>
      <TopSection
        labelList={[
          { title: "Home", path: "/landing-page" },
          { title: "Contract Maintenance Report" }, 
        ]}
        hideHeader={true}
        headerName={"Contract Maintenance Report"}
      />
      {
        isLoading ? (
          <Loader />
        ) : (
          <>
          {/* <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px 0 20px 0" }}>
            <ButtonComponent
              label="Download Contracts Tracking Report"
              bgColor="#000"
              color="#fff"
              maxWidth="400px"
              onClick={downloadCMTrackingReport}
            />
          </div> */}
          <div className="tab-part" style={{border: "1px solid #ccc", borderRadius: "8px"}}>
            <TabSection
                style={{
                maxWidth: "600px",
                marginBottom: "10px",
                marginTop: "10px",
                width: "100%",
                }}
                label={["Contract Maintenance Report", "Target Value Report"]}
                value={tab}
                handleChange={handleTabChange}
            />
            { tab === 0 ? (
            <div className="priceMemo_dataTable">
              <DataDetails 
                rowData={getAllReportingData?.cmChangesHisDtoList}
                onSelectionChanged={onSelectionChanged}
                rowSelection={rowSelection}
                setRowsData={setRowsData}
              />
              <div style={{ margin: "10px 0px 10px 10px", fontWeight: "bold" }}>
                Total Records: {rowsData > 0 ? rowsData : (getAllReportingData?.cmChangesHisDtoList?.length || 0)}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "30px",
                }}
              >
                <div>
                </div>
                { userRole !== "Viewer" &&
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100px",
                  }}
                >
                  <ButtonComponent
                    label="Download"
                    bgColor="#000"
                    color="#fff"
                    onClick={handleReportDownload}
                    disabled={btnDisabled}
                  />
                </div>
                }
              </div>
            </div>
            ) : (
              <div className="priceMemo_dataTable">
              <TargetValueDetails
                rowData={getAllTVReportsData?.cdTargetBatchJobDTOList}
                onSelectionChanged={onSelectionChanged}
                rowSelection={rowSelection}
                setTargetValueRowsData={setTargetValueRowsData}
              />
              <div style={{ margin: "10px 0px 10px 10px", fontWeight: "bold" }}>
                Total Records: {targetValueRowsData > 0 ? targetValueRowsData : (getAllTVReportsData?.cdTargetBatchJobDTOList?.length || 0)}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "30px",
                }}
              >
                <div>
                </div>
                { userRole !== "Viewer" &&
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100px",
                  }}
                >
                  <ButtonComponent
                    label="Download"
                    bgColor="#000"
                    color="#fff"
                    onClick={handleTargetValueReportDownload}
                    disabled={btnDisabled}
                  />
                </div>
                }
              </div>
            </div>
            )
            }
          </div>
          </>
        )
      }
    </>
  )
}

export default NewYearContractReports